import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Building2,
  X,
  RefreshCw,
  FileSpreadsheet,
  UserPlus,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import ClickableStatCard from "../../components/system/ClickableStatCard";
import TenantStatusPill, { TENANT_STATUSES } from "./TenantStatusPill";

function SimpleDialog({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3"
      data-testid={testid}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md">
        <Card variant="raised" className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="font-display font-black text-xl">{title}</div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"
              data-testid={`${testid}-close`}
            >
              <X size={20} />
            </button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

/**
 * Operator-only tenant command-center (Phase 2F refactor).
 *
 * The legacy version dumped Stores / Departments / Users inline below
 * the stat cards. Phase 2F moves every list into a dedicated drill-down
 * page so this view stays a true "command center":
 *
 *   - 8 clickable summary cards (each navigates to a focused list)
 *   - Tenant metadata + status + rename + first-admin CTA
 *   - Catalog imports entry point (unchanged)
 *
 * "Recent activity" + "Alerts requiring action" panels deferred per the
 * Phase 1 brief — they will land in a later phase.
 *
 * Phase 1 INVARIANTS:
 *   - No user mutation actions added.
 *   - "Mint reset link" lives ONLY on the drill-down Total Users page now.
 *   - All drill-down pages are operator-only and read-only.
 */
export default function TenantDetailPage() {
  const { cid } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [counts, setCounts] = useState({});
  const [usersCount, setUsersCount] = useState({ admins: 0, total: 0 });

  // Inline edits
  const [statusDraft, setStatusDraft] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [det, us] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/users`),
      ]);
      setCompany(det.data.company);
      setCounts(det.data.counts || {});
      setStatusDraft(det.data.company?.status || "trial");
      setRenameDraft(det.data.company?.name || "");
      const list = us.data.users || [];
      setUsersCount({
        admins: list.filter((u) => u.role === "admin").length,
        total: list.length,
      });
    } catch (e) {
      const msg = formatApiErrorDetail(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    load();
  }, [load]);

  const saveStatus = async () => {
    if (!statusDraft || statusDraft === company?.status) return;
    setStatusSaving(true);
    try {
      const { data } = await api.patch(`/platform/companies/${cid}`, {
        status: statusDraft,
      });
      setCompany(data.company);
      toast.success(`Status set to ${data.company.status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
      setStatusDraft(company?.status || "trial");
    } finally {
      setStatusSaving(false);
    }
  };

  const saveRename = async () => {
    const cleaned = renameDraft.trim();
    if (!cleaned || cleaned === company?.name) {
      setRenameOpen(false);
      return;
    }
    setRenameSaving(true);
    try {
      const { data } = await api.patch(`/platform/companies/${cid}`, {
        name: cleaned,
      });
      setCompany(data.company);
      toast.success("Tenant renamed");
      setRenameOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setRenameSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="tenant-detail-loading">
        <Card variant="raised" className="p-10 text-center">
          <div className="font-mono text-sm text-muted-foreground">
            Loading tenant…
          </div>
        </Card>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div data-testid="tenant-detail-error">
        <Link
          to="/platform"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink mb-4"
        >
          <ArrowLeft size={14} />
          Back to tenants
        </Link>
        <EmptyState
          icon={<Building2 size={36} />}
          title="Tenant not found"
          description={error || "We could not load this tenant."}
          action={
            <Button tier="secondary" onClick={() => nav("/platform")}>
              Back to tenants
            </Button>
          }
        />
      </div>
    );
  }

  const base = `/platform/companies/${cid}`;

  return (
    <div data-testid="tenant-detail-page">
      <div className="mb-4">
        <Link
          to="/platform"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="tenant-detail-back"
        >
          <ArrowLeft size={14} />
          Back to tenants
        </Link>
      </div>

      <PageHeader
        eyebrow={
          <span className="font-mono text-[11px]">
            id: <span data-testid="tenant-detail-id">{company.id}</span> ·
            slug: <span data-testid="tenant-detail-slug">{company.slug}</span>
          </span>
        }
        title={company.name}
        titleSuffix={
          <TenantStatusPill
            status={company.status}
            data-testid="tenant-detail-status-pill"
          />
        }
        actions={
          <>
            <Button
              tier="secondary"
              onClick={() => setRenameOpen(true)}
              data-testid="tenant-rename-button"
            >
              Rename
            </Button>
            <Button
              tier="secondary"
              onClick={load}
              data-testid="tenant-refresh-button"
            >
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button
              tier="primary"
              onClick={() => nav(`${base}/activate-admin`)}
              data-testid="tenant-activate-admin-button"
            >
              <UserPlus size={16} />{" "}
              {usersCount.admins === 0 ? "Create first admin" : "Invite user"}
            </Button>
          </>
        }
      />

      {/* Status updater */}
      <Card variant="raised" className="p-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="data-sku">Status</div>
          <Select
            value={statusDraft}
            onChange={(e) => setStatusDraft(e.target.value)}
            className="w-40"
            data-testid="tenant-status-select"
            disabled={statusSaving}
          >
            {TENANT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          <Button
            tier="secondary"
            size="sm"
            onClick={saveStatus}
            disabled={statusSaving || statusDraft === company.status}
            data-testid="tenant-status-save"
          >
            {statusSaving ? "Saving…" : "Save status"}
          </Button>
          <p className="font-mono text-[11px] text-muted-foreground ml-auto max-w-md text-right">
            Suspended / cancelled / archived blocks tenant logins immediately
            (operators stay in).
          </p>
        </div>
      </Card>

      {/* Command-center cards — Phase 2F. Each card is a click-to-drill
          navigation entry point. Order matches the operator brief:
          Stores · Active Users · Departments · Active Batches · Removals
          · Open Product Requests · Catalog Imports · Total Users. */}
      <h2 className="font-display font-black text-xl mb-3" data-testid="command-center-heading">
        Command center
      </h2>
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
        data-testid="command-center-cards"
      >
        <ClickableStatCard
          label="Stores"
          value={counts.stores}
          to={`${base}/stores`}
          testid="card-stores"
        />
        <ClickableStatCard
          label="Active users"
          value={counts.users_active}
          to={`${base}/users?status=active`}
          testid="card-users-active"
        />
        <ClickableStatCard
          label="Departments"
          value={counts.departments}
          to={`${base}/departments`}
          testid="card-departments"
        />
        <ClickableStatCard
          label="Active batches"
          value={counts.active_batches}
          to={`${base}/batches?status=active`}
          testid="card-active-batches"
        />
        <ClickableStatCard
          label="Removals (30d)"
          value={counts.removals_30d}
          to={`${base}/removals?days=30`}
          testid="card-removals-30d"
        />
        <ClickableStatCard
          label="Open product requests"
          value={counts.product_requests_open}
          to={`${base}/product-requests?status=pending`}
          testid="card-product-requests"
        />
        <ClickableStatCard
          label="Catalog imports"
          value={counts.catalog_imports}
          to={`${base}/catalog/imports`}
          testid="card-catalog-imports"
        />
        <ClickableStatCard
          label="Total users"
          value={counts.users_total ?? usersCount.total}
          to={`${base}/users`}
          testid="card-users-total"
        />
      </div>

      {/* Catalog Imports entry point card (kept as a more detailed
          stand-alone section because operators also need direct "New
          import" access without clicking through to the list). */}
      <Card
        variant="raised"
        className="p-4 mb-6 flex items-center justify-between gap-4 flex-wrap"
        data-testid="section-catalog-imports"
      >
        <div className="flex items-center gap-3 min-w-0">
          <FileSpreadsheet
            size={20}
            className="text-brand shrink-0"
            strokeWidth={2.25}
          />
          <div className="min-w-0">
            <div className="font-display font-black">Catalog imports</div>
            <p className="text-xs text-muted-foreground">
              Validate, preview, and commit catalog data into{" "}
              <strong>{company.name}</strong>. {counts.catalog_imports ?? 0}{" "}
              previous run{counts.catalog_imports === 1 ? "" : "s"}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            tier="secondary"
            size="sm"
            onClick={() => nav(`${base}/catalog/imports`)}
            data-testid="open-catalog-imports-button"
          >
            View imports
          </Button>
          <Button
            tier="primary"
            size="sm"
            onClick={() => nav(`${base}/catalog/imports/new`)}
            data-testid="new-catalog-import-button"
          >
            <Plus size={14} /> New import
          </Button>
        </div>
      </Card>

      {/* Rename dialog */}
      <SimpleDialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename tenant"
        testid="rename-dialog"
      >
        <div className="space-y-3">
          <Input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            autoFocus
            data-testid="rename-input"
          />
          <div className="flex gap-2">
            <Button
              tier="primary"
              onClick={saveRename}
              disabled={renameSaving || !renameDraft.trim()}
              data-testid="rename-save"
              className="flex-1"
            >
              {renameSaving ? "Saving…" : "Save"}
            </Button>
            <Button
              tier="tertiary"
              onClick={() => setRenameOpen(false)}
              disabled={renameSaving}
              data-testid="rename-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      </SimpleDialog>
    </div>
  );
}
